import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DEFAULT_ACCENT_HUE = 260;

export const RailEyebrow = ({ children }: { children: React.ReactNode }) => (
    <div className="inline-flex items-center gap-2 mb-5">
        <span className="relative flex w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-primary opacity-60 animate-ping" />
            <span className="relative rounded-full bg-primary w-2 h-2" />
        </span>
        <p className="font-v2-label text-xs text-primary/90 tracking-[0.25em]">
            {children}
        </p>
    </div>
);

interface RailPaginationProps {
    itemCount: number;
    activeIndex: number;
    onSelect: (index: number) => void;
    accentHues?: number[];
    ariaLabelPrefix?: string;
}

export const RailPagination = ({
    itemCount,
    activeIndex,
    onSelect,
    accentHues,
    ariaLabelPrefix = "slide",
}: RailPaginationProps) => (
    <motion.div
        className="flex items-center justify-center gap-5 mb-10 md:mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ amount: 0.3, margin: "-40px" }}
        transition={{ duration: 0.5, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
    >
        <span className="font-v2-label text-xs text-white/40 tabular-nums">
            <span className="text-white font-bold">
                {String(activeIndex + 1).padStart(2, "0")}
            </span>
            <span className="mx-2 text-white/20">—</span>
            {String(itemCount).padStart(2, "0")}
        </span>

        <div className="flex items-center gap-1.5">
            {Array.from({ length: itemCount }).map((_, i) => {
                const isActive = i === activeIndex;
                const hue = accentHues?.[i] ?? DEFAULT_ACCENT_HUE;
                return (
                    <button
                        key={i}
                        type="button"
                        aria-label={`${ariaLabelPrefix} ${i + 1}`}
                        onClick={() => onSelect(i)}
                        className="group/dot relative h-2 flex items-center"
                        data-cursor="hover"
                    >
                        <span
                            className="block h-1 rounded-full transition-all duration-500"
                            style={{
                                width: isActive ? "28px" : "8px",
                                background: isActive
                                    ? `hsl(${hue} 90% 60%)`
                                    : "hsl(0 0% 100% / 0.15)",
                                boxShadow: isActive
                                    ? `0 0 12px hsl(${hue} 90% 60% / 0.6)`
                                    : "none",
                            }}
                        />
                    </button>
                );
            })}
        </div>
    </motion.div>
);

export const RailEdgeFades = ({ isRtl }: { isRtl: boolean }) => (
    <>
        {/* start (logical) = left in LTR / right in RTL — dark at the screen edge, fading inward */}
        <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 start-0 w-12 md:w-24 z-10"
            style={{
                background: `linear-gradient(to ${isRtl ? "left" : "right"}, hsl(var(--background)) 0%, transparent 100%)`,
            }}
        />
        {/* end (logical) = right in LTR / left in RTL */}
        <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 end-0 w-12 md:w-24 z-10"
            style={{
                background: `linear-gradient(to ${isRtl ? "right" : "left"}, hsl(var(--background)) 0%, transparent 100%)`,
            }}
        />
    </>
);

interface RailArrowsProps {
    onPrev: () => void;
    onNext: () => void;
    canPrev: boolean;
    canNext: boolean;
    isRtl: boolean;
}

export const RailArrows = ({ onPrev, onNext, canPrev, canNext, isRtl }: RailArrowsProps) => (
    <>
        <button
            type="button"
            aria-label="previous"
            onClick={onPrev}
            disabled={!canPrev}
            className="hidden md:flex absolute start-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center rounded-full glass-v2 border border-white/10 text-white/80 hover:text-white hover:border-white/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            data-cursor="hover"
        >
            {isRtl ? <ChevronRight size={22} /> : <ChevronLeft size={22} />}
        </button>
        <button
            type="button"
            aria-label="next"
            onClick={onNext}
            disabled={!canNext}
            className="hidden md:flex absolute end-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center rounded-full glass-v2 border border-white/10 text-white/80 hover:text-white hover:border-white/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            data-cursor="hover"
        >
            {isRtl ? <ChevronLeft size={22} /> : <ChevronRight size={22} />}
        </button>
    </>
);

export const RailProgressBar = ({ progress }: { progress: number }) => (
    <div className="container mx-auto px-6 md:px-24">
        <div className="relative h-px w-full bg-white/[0.06] overflow-hidden rounded-full">
            <motion.div
                className="absolute inset-y-0 start-0 rounded-full"
                style={{
                    background:
                        "linear-gradient(90deg, hsl(260 90% 65%), hsl(190 90% 50%))",
                    width: `${Math.max(8, progress * 100)}%`,
                    boxShadow: "0 0 12px hsl(260 90% 65% / 0.5)",
                }}
                transition={{ type: "spring", stiffness: 120, damping: 20 }}
            />
        </div>
    </div>
);

export const railContainerClass = (needsScroll: boolean) =>
    `rail-scroll flex gap-6 md:gap-8 snap-x snap-mandatory scroll-smooth pb-10 focus-visible:outline-none ${
        needsScroll
            ? "overflow-x-auto px-6 md:px-24"
            : "overflow-x-hidden justify-center px-6"
    }`;

export const RailScrollbarHideStyle = () => (
    <style>{`.rail-scroll::-webkit-scrollbar{display:none}.rail-scroll{scrollbar-width:none}`}</style>
);
